import { LabelCount } from '@/components/Graphs'
import { Report, ReportFilterField, REPORT_TYPE } from '@/types/reports'
import {
  APPLICATION,
  ApplicationType,
  DATA_RESIDENCIES,
  EventTicket,
} from '@saasalerts/common/shared'
import { LIST_OF_REPORTS, PRODUCT_IMAGES } from '@/utils/constants'
import { momentUTC } from '@/utils/momentUTC'
import { get } from 'lodash'
import moment, { Moment } from 'moment-timezone'
import {
  Children,
  ElementType,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react'
import { Organization } from '@saasalerts/common/manage'
import { AugmentedOrganization } from '@/utils/fortify'
import { FortifyActionType } from '@/components/Pages/ThreatMitigation'

export const getLabelsFromLabelCounts = (
  labelCounts: LabelCount[]
): string[] => {
  const labels = labelCounts
    .map((labelCount) => labelCount.label)
    .filter((label) => label !== 'N/A')
  return labels
}

export const getApplicationNameByType = (productType?: string) => {
  if (productType) {
    return APPLICATION[productType as ApplicationType].name
  }
  return ''
}

export const getAllDatabaseIds = () =>
  DATA_RESIDENCIES.map((dataResidency) => dataResidency.databaseId)

// Join classes
export const classNames = (...classes: string[]): string => {
  return classes.filter(Boolean).join(' ')
}

export const formatToUSDate = (
  date: string | Date | undefined | Moment | number
): string => {
  if (date === undefined) {
    return ''
  }
  return momentUTC(date).format('MM/DD/YYYY')
}

export const getLocalDate = (
  date: string | Date | undefined | Moment
): string => {
  if (date === undefined) {
    return ''
  }
  return momentUTC(date).tz(moment.tz.guess()).format('MM/DD/YYYY')
}

export const formatToComputerDate = (
  date: string | Date | undefined | Moment
): string => {
  if (date === undefined) {
    return ''
  }
  return momentUTC(date).format('YYYY-MM-DD')
}

export const formatToLocalComputerDate = (
  date: string | Date | undefined | Moment
): string => {
  if (date === undefined) {
    return ''
  }
  return moment(date).format('YYYY-MM-DD')
}

export const formatStartDateToTimezone = (
  startDate: string | Date | undefined | Moment,
  timezone?: string
): string => {
  if (startDate === undefined) {
    return ''
  }

  return moment(startDate)
    .tz(timezone ?? moment.tz.guess())
    .startOf(`day`)
    .format('YYYY-MM-DDTHH:mm:ssZ')
}

export const formatEndDateToTimezone = (
  endDate: string | Date | undefined | Moment,
  timezone?: string
): string => {
  if (endDate === undefined) {
    return ''
  }
  return moment(endDate)
    .tz(timezone ?? moment.tz.guess())
    .endOf(`day`)
    .format('YYYY-MM-DDTHH:mm:ssZ')
}

export const isDateBetween = ({
  startDate,
  endDate,
  date,
}: {
  startDate: string | Date
  endDate: string | Date
  date: string
}): boolean => {
  if (!momentUTC(date).isValid()) {
    return false
  }

  if (!momentUTC(startDate).isValid()) {
    return false
  }

  if (!momentUTC(endDate).isValid()) {
    return false
  }

  const eventDate = momentUTC(date)
  return eventDate.isBetween(momentUTC(startDate), momentUTC(endDate))
}

export const findChildByType = (
  children: ReactNode | ReactNode[],
  type: ElementType
): ReactElement | undefined => {
  const kid = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === type
  )
  if (isValidElement(kid)) {
    return kid
  }
  return undefined
}

export const getReportById = (reportId: string): Report | undefined => {
  return LIST_OF_REPORTS.find((report) => report.id === reportId)
}

export const filterMSOrganizations = (
  organizations: (Organization | AugmentedOrganization)[]
) => {
  const hasMSOrganizations = organizations
    ?.filter((organization) => {
      const productName = organization.products?.some(
        (product) => product?.name === APPLICATION.MS.name
      )
      if (productName) return true
    })
    ?.filter((organization) => {
      if (organization) return true
    })

  return hasMSOrganizations
}

export const filterOrganizationsByApplicationType = (
  organizations: (Organization | AugmentedOrganization)[],
  applicationType?: string
) => {
  return organizations.filter((organization) =>
    organization.products?.some((product) => {
      return product.type === applicationType
    })
  )
}

export const isOperationalReport = (reportId?: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId && report.reportType === REPORT_TYPE.OPERATIONAL
  )
}

export const formatTickets = (tickets?: EventTicket[] | undefined) => {
  return tickets
    ?.map((ticket) => {
      const ticketTypeAndNumber = ticket.type + ' ' + ticket.ticketNumber
      const type = ticket.type
      return Boolean(ticket.ticketNumber) ? ticketTypeAndNumber : type
    })
    .join(', ')
}

export const isConfigurationReport = (reportId?: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId && report.reportType === REPORT_TYPE.CONFIGURATION
  )
}

export const hasDateRangeFilter = (reportId?: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId &&
      report.filters.includes(ReportFilterField.DATE_RANGE)
  )
}

export const getProductImageByProductType = (productType: string): string => {
  return get(PRODUCT_IMAGES, productType)
}

/**
 * @param {date|moment} startDate The start date
 * @param {date|moment} endDate The end date
 * @param {string} type The range type. eg: 'days', 'hours' etc
 */
export const getDateRange = (
  startDate: string | Date | Moment,
  endDate: string | Date | Moment,
  type: 'days'
) => {
  const fromDate = momentUTC(startDate)
  const toDate = momentUTC(endDate).add(1, type)
  const diff = toDate.diff(fromDate, type)
  const range = []
  for (let i = 0; i < diff; i++) {
    range.push(
      momentUTC(startDate)
        .utcOffset((startDate as string).slice(-6))
        .add(i, type)
        .format('YYYY-MM-DD')
    )
  }
  return range
}

export const hasApplicationTypeFilter = (reportId: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId &&
      report.filters.includes(ReportFilterField.APPLICATION)
  )
}

export const hasOrganizationFilter = (reportId: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId &&
      report.filters.includes(ReportFilterField.ORGANIZATION)
  )
}

export const hasEmailFilter = (reportId: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId &&
      report.filters.includes(ReportFilterField.ACCOUNT_EMAIL)
  )
}

export const hasSnapshotFilter = (reportId: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId &&
      report.filters.includes(ReportFilterField.FORTIFY_SNAPSHOT)
  )
}

export const isFortifyReport = (reportId: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId &&
      report.tags?.some((tag) => tag.title === 'Fortify')
  )
}

export const isUnifyReport = (reportId: string): boolean => {
  return LIST_OF_REPORTS.some(
    (report) =>
      report.id === reportId && report.tags?.some((tag) => tag.title === 'Unify')
  )
}

export const getLocalDateTime = (date: Moment | string | undefined): string => {
  if (date === undefined) {
    return ''
  }
  return momentUTC(date).tz(moment.tz.guess()).format('MM/DD/YYYY HH:mm:ss z')
}

export const formatEventWithLocalDate = <
  T extends Record<string, unknown> & {
    formatted?: Record<string, unknown>
    date: string | Moment | undefined
  }
>(
  event: T
) => {
  return {
    ...event,
    formatted: {
      ...event?.formatted,
      date: getLocalDateTime(event.date),
    },
  }
}

// for each date from start date and end date check if it exists in the dates array
// if it does not exist, add it with 0 values
export const fillEmptyDates = <T extends Record<string, unknown>>({
  data,
  startDate,
  endDate,
  emptyDataFormat,
}: {
  data: T[]
  startDate: string
  endDate: string
  emptyDataFormat: Omit<T, 'date'>
}) => {
  const dateRange = getDateRange(startDate, endDate, 'days')
  const filledDates = dateRange.map((date) => {
    const existingDate = data?.find((d) => {
      const formattedDate = momentUTC(d?.date as string)
        .utcOffset(startDate.slice(-6))
        .format('YYYY-MM-DD')
      return formattedDate === date
    })
    if (existingDate) {
      return existingDate
    }
    return {
      ...emptyDataFormat,
      date,
    }
  })
  return filledDates
}

export const getReportTitle = ({
  reportName,
  organizationName,
  startDate,
  endDate,
}: {
  reportName?: string
  organizationName?: string
  startDate?: Date | string
  endDate?: Date | string
}) => {
  const title = `${reportName ?? ''}-${
    organizationName ? '-' + organizationName : ''
  }${startDate ? '-' + startDate : ''}${endDate ? '-' + endDate : ''}`
  return title
    .toLowerCase()
    .replaceAll(' ', '-')
    .replace('---', '-')
    .replace('--', '-')
}
